// src/components/discovery/CandidatePanel.jsx
// SlidePanel detail view for a single discovery candidate.

import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Link,
  Collapse,
  Divider,
  Alert,
} from '@mui/material';
import SlidePanel from '../common/SlidePanel';
import ConfidenceDots from './ConfidenceDots';
import DedupChip from './DedupChip';
import { approveCandidate, rejectCandidate, deferCandidate,
         suggestProductsForCandidate, searchProducts,
         checkCandidateDuplicate } from '../../api/discovery';
import ProductSuggestPicker from '../common/ProductSuggestPicker';
import { FS, SP } from '../../theme/tokens';
import { sourceTierLabel } from '../../utils/labels';

// Thin label/value row used throughout the panel sections.
function InfoRow({ label, children }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.5 }} alignItems="flex-start">
      <Typography sx={{ fontSize: FS.sm, color: 'text.secondary', minWidth: 120, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {children}
      </Box>
    </Stack>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      sx={{ fontSize: FS.xs, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: 'text.secondary', mb: 0.5 }}
    >
      {children}
    </Typography>
  );
}

export default function CandidatePanel({ candidate, onClose, onApproved, onRejected, onDeferred }) {
  const [actionLoading, setActionLoading] = useState(null); // 'approve'|'reject'|'defer'|null
  const [altOpen, setAltOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [dupRec, setDupRec] = useState(null);
  // W4: der Grund, wenn die Freigabe ein Ziel INAKTIV angelegt hat.
  const [approveNote, setApproveNote] = useState(null);

  // On-open Reject signals (derived, always fresh from the candidate).
  const openRec = !candidate
    ? null
    : candidate.likely_non_product
      ? { kind: 'non_product', action: 'reject',
          message: `This looks like a ${candidate.non_product_reason || 'non-product'} page, not a product page — recommend Reject.` }
      : !candidate.matched_market_id
        ? { kind: 'no_market', action: 'reject',
            message: 'brewout has no market configured for this shop — it can\'t be tracked. Recommend Reject.' }
        : null;
  const recommendation = openRec || dupRec;

  const needsSku = candidate && !candidate.matched_sku;

  useEffect(() => {
    setActionError(null);
    setApproveNote(null);
    setSelectedProduct(null);
    setSuggestions([]);
    if (!candidate || candidate.matched_sku) return;
    let active = true;
    setSuggestLoading(true);
    suggestProductsForCandidate(candidate.id)
      .then(res => { if (active) setSuggestions(res?.suggestions || []); })
      .catch(() => { if (active) setSuggestions([]); })
      .finally(() => { if (active) setSuggestLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id]); // key on id, NOT the object ref — avoids re-firing the LLM every parent render

  useEffect(() => {
    setDupRec(null);
    if (!candidate || openRec) return;
    const sku = selectedProduct?.sku;
    if (!sku || !candidate.existing_competitor_id) return;
    let active = true;
    const t = setTimeout(() => {
      checkCandidateDuplicate(candidate.id, sku)
        .then(res => {
          if (active && res?.duplicate) {
            setDupRec({
              kind: 'duplicate', action: 'defer',
              message: `Already tracked for ${res.competitor_name || 'this competitor'} (${res.market_code || '?'}) — already a scrape target. Recommend Defer.`,
            });
          }
        })
        .catch(() => {});
    }, 300);
    return () => { active = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidate?.id, selectedProduct?.sku]); // openRec is derived from candidate.id; not a separate dep

  const busy = actionLoading !== null;
  const recAction = recommendation?.action;
  // 🔴 W4: steht der Hinweis, ist der Kandidat freigegeben — dann ist keine der
  // drei Aktionen mehr sinnvoll (ein zweiter Klick liefe in „Already tracked").
  const approveDisabled = busy || !!approveNote || (needsSku && !selectedProduct) || !!recommendation;
  const rejectLabel = recAction === 'reject'
    ? (recommendation.kind === 'non_product' ? 'Reject — not a product' : 'Reject — no market')
    : 'Reject';
  const deferLabel = recAction === 'defer' ? 'Defer — already tracked' : 'Defer';

  async function handleApprove() {
    setActionError(null);
    setActionLoading('approve');
    try {
      const res = await approveCandidate(candidate.id, {
        reviewedBy: 'ui',
        skuOverride: candidate.matched_sku ? null : selectedProduct?.sku || null,
      });
      // 🔴 W4: die Route legt einen `web_research`-Kandidaten INAKTIV an (das
      // Drei-Stufen-Tor, §6.3 Nr. 4) und SAGT das im Rumpf (`aktiv`, `grund`).
      // Wer den Rumpf nicht liest, meldet „Approved" und verschweigt, dass
      // nichts beobachtet wird — das Panel bleibt deshalb offen und nennt den
      // Grund.
      if (res && res.aktiv === false) {
        setApproveNote(
          res.grund === 'drei_stufen_tor'
            ? 'Approved — inactive until the 3-stage check passes.'
            : `Approved — inactive (${res.grund || 'reason not reported'}).`);
        return;
      }
      onApproved?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Approve failed. Please retry.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleReject() {
    setActionError(null);
    setActionLoading('reject');
    try {
      await rejectCandidate(candidate.id, { reviewedBy: 'ui' });
      onRejected?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Reject failed. Please retry.');
    } finally {
      setActionLoading(null);
    }
  }

  async function handleDefer() {
    setActionError(null);
    setActionLoading('defer');
    try {
      await deferCandidate(candidate.id, { reviewedBy: 'ui' });
      onDeferred?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || 'Defer failed. Please retry.');
    } finally {
      setActionLoading(null);
    }
  }

  const alternatives = candidate?.match_alternatives;
  const hasAlts = Array.isArray(alternatives) && alternatives.length > 0;

  return (
    <SlidePanel
      open={!!candidate}
      onClose={onClose}
      title={candidate?.shop_host || 'Candidate'}
      subtitle={candidate?.shop_name || undefined}
    >
      {candidate && (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* ── Scrollable content ──────────────────────────────── */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: SP.card, py: SP.sm }}>

            {/* 1. Shop info */}
            <SectionTitle>Shop</SectionTitle>
            <InfoRow label="Host">
              <Typography sx={{ fontSize: FS.base }}>{candidate.shop_host}</Typography>
            </InfoRow>
            {candidate.shop_name && (
              <InfoRow label="Name">
                <Typography sx={{ fontSize: FS.base }}>{candidate.shop_name}</Typography>
              </InfoRow>
            )}
            {candidate.product_url && (
              <InfoRow label="Product URL">
                <Link
                  href={candidate.product_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontSize: FS.sm, wordBreak: 'break-all' }}
                >
                  {candidate.product_url}
                </Link>
              </InfoRow>
            )}
            {candidate.page_title && (
              <InfoRow label="Page title">
                <Typography sx={{ fontSize: FS.sm, color: 'text.secondary' }}>
                  {candidate.page_title}
                </Typography>
              </InfoRow>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* 2. Match info */}
            <SectionTitle>Match</SectionTitle>
            <InfoRow label="SKU">
              <Typography sx={{ fontSize: FS.base, fontFamily: 'monospace' }}>
                {candidate.matched_sku || '—'}
              </Typography>
            </InfoRow>
            <InfoRow label="Method">
              <Typography sx={{ fontSize: FS.base }}>{candidate.match_method || '—'}</Typography>
            </InfoRow>
            <InfoRow label="Confidence">
              <ConfidenceDots
                confidence={candidate.match_confidence}
                method={candidate.match_method}
              />
            </InfoRow>
            {needsSku && (
              <Box sx={{ mt: 1 }}>
                <ProductSuggestPicker
                  value={selectedProduct}
                  onChange={setSelectedProduct}
                  suggestions={suggestions}
                  suggestLoading={suggestLoading}
                  onSearch={searchProducts}
                />
              </Box>
            )}
            {hasAlts && (
              <InfoRow label="Alternatives">
                <Box>
                  <Typography
                    component="span"
                    onClick={() => setAltOpen(o => !o)}
                    sx={{ fontSize: FS.sm, color: 'primary.main', cursor: 'pointer' }}
                  >
                    {altOpen ? 'Hide' : `Show ${alternatives.length}`}
                  </Typography>
                  <Collapse in={altOpen}>
                    <Box sx={{ mt: 0.5 }}>
                      {alternatives.map((alt, i) => (
                        <Typography key={i} sx={{ fontSize: FS.sm, color: 'text.secondary', fontFamily: 'monospace' }}>
                          {typeof alt === 'string' ? alt : JSON.stringify(alt)}
                        </Typography>
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              </InfoRow>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* 3. Dedup */}
            <SectionTitle>Dedup</SectionTitle>
            <InfoRow label="Status">
              <DedupChip dedupStatus={candidate.dedup_status} />
            </InfoRow>
            {candidate.existing_competitor_id && (
              <InfoRow label="Competitor ID">
                <Typography sx={{ fontSize: FS.sm, fontFamily: 'monospace', color: 'text.secondary' }}>
                  {candidate.existing_competitor_id}
                </Typography>
              </InfoRow>
            )}
            {candidate.existing_target_id && (
              <InfoRow label="Target ID">
                <Typography sx={{ fontSize: FS.sm, fontFamily: 'monospace', color: 'text.secondary' }}>
                  {candidate.existing_target_id}
                </Typography>
              </InfoRow>
            )}

            <Divider sx={{ my: 1.5 }} />

            {/* 4. Raw data */}
            <SectionTitle>Raw data</SectionTitle>
            <InfoRow label="Price">
              <Typography sx={{ fontSize: FS.base }}>
                {candidate.raw_price != null
                  ? `${candidate.raw_price} ${candidate.raw_currency || ''}`.trim()
                  : '—'}
              </Typography>
            </InfoRow>
            <InfoRow label="Availability">
              <Typography sx={{ fontSize: FS.base }}>{candidate.raw_availability || '—'}</Typography>
            </InfoRow>
            {candidate.observed_ean && (
              <InfoRow label="EAN">
                <Typography sx={{ fontSize: FS.base, fontFamily: 'monospace' }}>
                  {candidate.observed_ean}
                </Typography>
              </InfoRow>
            )}
            <InfoRow label="Source">
              <Typography sx={{ fontSize: FS.base }}>{sourceTierLabel(candidate.source_tier, '—')}</Typography>
            </InfoRow>
          </Box>

          {/* ── Action bar ─────────────────────────────────────── */}
          <Box
            sx={{
              px: SP.card,
              py: SP.sm,
              borderTop: '1px solid',
              borderColor: 'divider',
              flexShrink: 0,
            }}
          >
            {recommendation && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1, fontSize: FS.sm }}>
                {recommendation.message}
              </Alert>
            )}
            {actionError && (
              <Alert severity="error" variant="outlined" sx={{ mb: 1, fontSize: FS.sm }}>
                {actionError}
              </Alert>
            )}
            {approveNote && (
              <Alert severity="info" variant="outlined" sx={{ mb: 1, fontSize: FS.sm }}>
                {approveNote}
              </Alert>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="success"
                size="small"
                disabled={approveDisabled}
                onClick={handleApprove}
                sx={{ flex: 1 }}
              >
                {actionLoading === 'approve' ? '…' : 'Approve'}
              </Button>
              <Button
                variant={recAction === 'reject' ? 'contained' : 'outlined'}
                color="error"
                size="small"
                disabled={busy || !!approveNote}
                onClick={handleReject}
                sx={{ flex: recAction === 'reject' ? 2 : 1, whiteSpace: 'normal', lineHeight: 1.2 }}
              >
                {actionLoading === 'reject' ? '…' : rejectLabel}
              </Button>
              <Button
                variant={recAction === 'defer' ? 'contained' : 'outlined'}
                color={recAction === 'defer' ? 'primary' : 'inherit'}
                size="small"
                disabled={busy || !!approveNote}
                onClick={handleDefer}
                sx={{ flex: recAction === 'defer' ? 2 : 1, whiteSpace: 'normal', lineHeight: 1.2 }}
              >
                {actionLoading === 'defer' ? '…' : deferLabel}
              </Button>
            </Stack>
            {needsSku && !selectedProduct && !recommendation && (
              <Typography sx={{ fontSize: FS.xs, color: 'text.secondary', mt: 0.5 }}>
                Assign a product above to approve.
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </SlidePanel>
  );
}
